import React, { useMemo, useState } from 'react'

// Helper function to format configuration values for display
function formatConfigValue(value) {
  if (value === undefined) {
    return <span className="config-changes-table-undefined-value">‹undefined›</span>
  }
  if (value === null || value === 'null') { // Treat JS null and the string "null" the same for display
    return 'null'
  }
  return JSON.stringify(value)
}

// Utility to diff two config objects
function getConfigDiff(prev, curr) {
  if (!prev) return { type: 'initial', changes: [] }
  const changes = []
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(curr)])
  for (const key of allKeys) {
    if (!(key in prev)) {
      // Only add if the new value is not null/undefined
      if (curr[key] !== null && curr[key] !== 'null' && curr[key] !== undefined) {
        changes.push({ key, type: 'added', newValue: curr[key] })
      }
    } else if (!(key in curr)) {
      // Only add if the old value was not null/undefined
      if (prev[key] !== null && prev[key] !== 'null' && prev[key] !== undefined) {
        changes.push({ key, type: 'removed', oldValue: prev[key] })
      }
    } else if (prev[key] !== curr[key]) {
      // Skip null -> null changes (including string "null" -> null, null -> "null", etc.)
      const oldIsNull = prev[key] === null || prev[key] === 'null'
      const newIsNull = curr[key] === null || curr[key] === 'null'
      if (!(oldIsNull && newIsNull)) {
        changes.push({ key, type: 'changed', oldValue: prev[key], newValue: curr[key] })
      }
    }
  }
  return { type: 'diff', changes }
}

/**
 * ConfigChangesTable
 * Displays a table of configuration changes, styled similarly to FaultsTable.
 * @param {Object[]} configs - Array of config snapshot objects (from /api/historical-configs)
 */
const ConfigChangesTable = React.memo(({ configs = [], onViewRelatedData }) => {
  const [filter, setFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortField, setSortField] = useState('timestamp')
  const [sortDirection, setSortDirection] = useState('desc')

  const rows = useMemo(() => {
    let prevConfig = null
    return configs.map((cfg) => {
      const diff = getConfigDiff(prevConfig, cfg.config_json)
      prevConfig = cfg.config_json
      return {
        timestamp: cfg.time,
        config: cfg.config_json,
        diff,
      }
    })
  }, [configs])

  const filteredRows = useMemo(() => {
    if (!filter) return rows
    const terms = filter.toLowerCase().split(/\s+/).filter(Boolean)
    return rows.filter(row =>
      terms.every(term =>
        (row.timestamp && String(row.timestamp).toLowerCase().includes(term)) ||
        row.diff.changes.some(chg => chg.key.toLowerCase().includes(term)),
      ),
    )
  }, [rows, filter])

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows]
    arr.sort((a, b) => {
      let cmp = 0
      if (sortField === 'timestamp') {
        cmp = new Date(a.timestamp) - new Date(b.timestamp)
      } else {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''))
      }
      return sortDirection === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filteredRows, sortField, sortDirection])

  const totalPages = Math.ceil(sortedRows.length / pageSize) || 1
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [sortedRows, currentPage, pageSize])

  React.useEffect(() => { setCurrentPage(1) }, [filter, pageSize])

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  if (!rows.length) return null

  return (
    <div className="mt-4">
      <div className="mb-2">
        <h5 className="mb-0 fw-bold">
          <i className="bi bi-gear me-2"></i>Configuration Changes
        </h5>
      </div>
      <div className="mb-2">
        <small className="text-muted">Click a column header to sort. Click again to reverse order.</small>
      </div>
      <div className="row mb-2">
        <div className="col-md-6 config-changes-table-filter-input-container">
          <input
            className={`form-control form-control-sm ${filter ? 'input-filter-padding' : ''}`}
            type="text"
            placeholder="Filter by time or changed keys..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          {filter ? (
            <button
              type="button"
              className="btn btn-sm btn-link config-changes-table-clear-filter-button"
              aria-label="Clear filter"
              onClick={() => setFilter('')}
            >
              <i className="bi bi-x config-changes-table-clear-filter-icon"></i>
            </button>
          ) : null}
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover align-middle">
          <thead>
            <tr>
              <th 
                className="config-changes-table-header-sortable"
                onClick={() => handleSort('timestamp')}
                aria-sort={sortField === 'timestamp' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Time
                <span className="config-changes-table-sort-indicator">
                  {sortField === 'timestamp' ? (sortDirection === 'asc' ? '▲' : '▼') : <span className="config-changes-table-sort-indicator-hidden">▲</span>}
                </span>
              </th>
              <th className="config-changes-table-header-changes">Changes</th>
              <th className="config-changes-table-header-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row) => (
              <tr key={row.timestamp}>
                <td className="config-changes-table-cell-data">{row.timestamp ? new Date(row.timestamp).toLocaleString() : row.timestamp}</td>
                <td className="config-changes-table-cell-data">
                  {row.diff.type === 'initial' ? (
                    <span className="text-muted">Initial configuration</span>
                  ) : row.diff.changes.length === 0 ? (
                    <span className="text-muted">No changes</span>
                  ) : (
                    <ul className="mb-0 list-unstyled">
                      {row.diff.changes.map((chg) => (
                        <li key={chg.key} className="mb-1">
                          <span className="config-changes-table-change-key">{chg.key}:</span>
                          {chg.type === 'added' && (
                            <span className="text-success">
                              added <span className="config-changes-table-change-added-value">{formatConfigValue(chg.newValue)}</span>
                            </span>
                          )}
                          {chg.type === 'removed' && (
                            <span className="text-danger">
                              removed <span className="config-changes-table-change-removed-value">{formatConfigValue(chg.oldValue)}</span>
                            </span>
                          )}
                          {chg.type === 'changed' && (
                            <span>
                              <span className="config-changes-table-change-old-value">{formatConfigValue(chg.oldValue)}</span>
                              <span className="config-changes-table-change-arrow">→</span>
                              <span className="config-changes-table-change-new-value">{formatConfigValue(chg.newValue)}</span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="align-top">
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    title="View Related Data"
                    onClick={() => {
                      const args = { device: null, measurement: null, time: new Date(row.timestamp).toLocaleString(), minutesBefore: 5, minutesAfter: 60 }
                      onViewRelatedData && onViewRelatedData(args)
                    }}
                  >
                    <i className="bi bi-graph-up me-1"></i>View Related Data
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    title="Download JSON"
                    onClick={() => {
                      const blob = new Blob([JSON.stringify(row.config, null, 2)], { type: 'application/json' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `config-${row.timestamp}.json`
                      document.body.appendChild(a)
                      a.click()
                      setTimeout(() => {
                        URL.revokeObjectURL(url)
                        document.body.removeChild(a)
                      }, 100)
                    }}
                  >
                    <i className="bi bi-download me-1"></i>Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <nav className="d-flex justify-content-between align-items-center mt-2" aria-label="Configuration changes table navigation">
        <div>
          <label htmlFor="config-page-size" className="visually-hidden">Configuration changes per page</label>
          <select
            id="config-page-size"
            className="form-select form-select-sm d-inline w-auto me-2"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
          <span className="text-muted">Page {currentPage} of {totalPages}</span>
        </div>
        <div>
          <button
            className="btn btn-sm btn-outline-secondary me-1"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page of configuration changes"
          >
            Prev
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page of configuration changes"
          >
            Next
          </button>
        </div>
      </nav>
    </div>
  )
})

ConfigChangesTable.displayName = 'ConfigChangesTable'

export default ConfigChangesTable
